import { defineStore } from 'pinia'
import axiosSalesAi, { type _AxiosError } from './api'
import { createDiscreteApi } from 'naive-ui'
import {
  ConversationAgent,
  CONVERSATION_TITLE_MAX_LENGTH,
  type ActiveTxResponse,
  type ChatMessage,
  type ChatMessageAdditionalData,
  type ChatTransaction,
  type Conversation,
  type ConversationDetail,
  type GetConversationsQuery,
  type GetHistoryQuery,
  type MaguchiAgentCustomerGroup,
  type MaguchiAgentStatusListResponse,
  type RunStep,
} from '@/types/conversation.type'
import type { PaginationMeta } from '@/types/pagination.type'

const { message } = createDiscreteApi(['message'])

interface State {
  currentConversation: ConversationDetail | null
  currentWorkflowConversation: ConversationDetail | null
  conversations: Conversation[]
  conversations_stats: PaginationMeta | null
  activeTx: ChatTransaction | null
  maguchiAgentStatuses: MaguchiAgentCustomerGroup[]
}
export const useConversationStore = defineStore('conversationStore', {
  state: (): State => ({
    currentConversation: null,
    currentWorkflowConversation: null,
    conversations: [],
    conversations_stats: null,
    activeTx: null,
    maguchiAgentStatuses: [],
  }),
  getters: {},
  actions: {
    // chatbot
    clear_current_conversation() {
      this.$patch((state) => {
        state.currentConversation = null
      })
    },
    // 本人の会話だけを返す
    get_history(json: GetHistoryQuery, skipSave = false) {
      return new Promise<Conversation[]>((resolve, reject) => {
        axiosSalesAi
          .get('/conversations/history', { params: json })
          .then((res) => {
            if (!skipSave) {
              this.$patch((state) => {
                state.conversations = res.data.items
                state.conversations_stats = res.data.meta
              })
            }
            resolve(res.data.items)
          })
          .catch((err: _AxiosError) => {
            message.error('Failed to load data')
            console.log(err)
            reject(err)
          })
      })
    },
    get_one_history(conversationId: number) {
      return axiosSalesAi
        .get(`/conversations/history/${conversationId}`)
        .then((res: { data: ConversationDetail }) => {
          this.set_current_conversation(res.data)
        })
        .catch((err: _AxiosError) => {
          console.log(err)
        })
    },
    set_current_conversation(data: ConversationDetail) {
      this.currentConversation = { ...data }
    },
    start(title: string, agent: ConversationAgent) {
      if (agent === ConversationAgent.orderEntryWorkflow) {
        throw new Error('orderEntryWorkflow is not supported')
      }
      return new Promise<number>((resolve, reject) => {
        axiosSalesAi
          .post('/conversations', {
            title: title.slice(0, CONVERSATION_TITLE_MAX_LENGTH),
            agent: agent,
          })
          .then((res: { data: ConversationDetail }) => {
            this.currentConversation = { ...res.data }
            this.$patch((state) => {
              state.conversations.unshift(res.data)
            })
            resolve(res.data.id)
          })
          .catch((err: _AxiosError) => {
            message.error('An error occurred')
            console.log(err)
            reject(err)
          })
      })
    },
    fetch_active_tx(conversationId: number, lastMessageId?: number) {
      return new Promise<void>((resolve, reject) => {
        axiosSalesAi
          .get(`/conversations/${conversationId}/chat-transactions/active-tx`, {
            params: {
              lastMessageId,
            },
            backgroundRequest: true,
          })
          .then((res: { data: ActiveTxResponse }) => {
            this.$patch((state) => {
              if (state.currentConversation && res.data.newMessages.length > 0) {
                const existingIds = new Set(state.currentConversation.chatMessages.map((m) => m.id))
                const newMsgs = res.data.newMessages.filter((m) => !existingIds.has(m.id))
                state.currentConversation.chatMessages.push(...newMsgs)
              }
              state.activeTx = res.data.activeTx
            })
            resolve()
          })
          .catch((err: _AxiosError) => {
            message.error('Failed to load run status')
            console.error(err)
            reject(err)
          })
      })
    },
    postChatMessage(
      text: string,
      conversationId: number,
      files?: File[],
      additionalData?: ChatMessageAdditionalData,
    ) {
      const formData = new FormData()
      formData.append('message', text)
      formData.append('data', JSON.stringify(additionalData ?? {}))
      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append('files', file)
        })
      }
      return new Promise<{
        tx: ChatTransaction
        messages: ChatMessage[]
      }>((resolve, reject) => {
        axiosSalesAi
          .post(`/conversations/${conversationId}/chat-messages`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then(
            (res: {
              data: {
                tx: ChatTransaction
                messages: ChatMessage[]
              }
            }) => {
              this.$patch((state) => {
                if (state.currentConversation?.id == res.data.tx.conversationId) {
                  state.currentConversation.chatTransactions.push(res.data.tx)
                  // polling (fetch_active_tx) と並走した場合、同じ user message が
                  // 先に push されていることがあるため id で重複弾き
                  for (const m of res.data.messages) {
                    if (
                      state.currentConversation.chatMessages.findIndex((x) => x.id === m.id) < 0
                    ) {
                      state.currentConversation.chatMessages.push(m)
                    }
                  }
                  // Set activeTx for immediate polling
                  state.activeTx = res.data.tx
                }
              })
              resolve(res.data)
            },
          )
          .catch((err: _AxiosError) => {
            message.error('An error occurred')
            console.log('err', err)
            reject(err)
          })
      })
    },
    getRunSteps(conversationId: number, txId: number) {
      return new Promise<RunStep[]>((resolve, reject) => {
        axiosSalesAi
          .get(`/conversations/${conversationId}/chat-transactions/${txId}/run-steps`)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err: _AxiosError) => {
            message.error('Failed to load run steps')
            console.error(err)
            reject(err)
          })
      })
    },
    getWorkingMemory(conversationId: number, txId: number) {
      return new Promise<string>((resolve, reject) => {
        axiosSalesAi
          .get(`/conversations/${conversationId}/chat-transactions/${txId}/working-memory`)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err: _AxiosError) => {
            message.error('Failed to load working memory')
            console.error(err)
            reject(err)
          })
      })
    },
    getFileDownloadUrl(key: string, conversationId: number) {
      return new Promise<string>((resolve, reject) => {
        axiosSalesAi
          .get(`/conversations/files/download-url`, { params: { key, conversationId } })
          .then((res: { data: { downloadUrl: string; expiresAt: string } }) => {
            resolve(res.data.downloadUrl)
          })
          .catch((err: _AxiosError) => {
            message.error('Failed to get file download URL')
            console.error(err)
            reject(err)
          })
      })
    },
    // maguchi-agent 稼働状況（customer 単位に集約した一覧）
    clearMaguchiAgentStatuses() {
      this.$patch((state) => {
        state.maguchiAgentStatuses = []
      })
    },
    fetchMaguchiAgentStatuses() {
      axiosSalesAi
        .get('/conversations/maguchi-agents')
        .then((res: { data: MaguchiAgentStatusListResponse }) => {
          this.$patch((state) => {
            state.maguchiAgentStatuses = res.data.items
          })
        })
        .catch((err: _AxiosError) => {
          message.error('Failed to load Iris status.')
          console.log(err)
        })
    },
    exitMaguchiAgentConversation(conversationId: number) {
      return new Promise<void>((resolve, reject) => {
        axiosSalesAi
          .post(`/conversations/maguchi-agents/${conversationId}/exit`)
          .then(() => {
            // exit 済みは一覧から除外される仕様。state からも当該 conversation を落とし、
            // 会話が 0 件になった customer group ごと除去する。
            this.$patch((state) => {
              state.maguchiAgentStatuses = state.maguchiAgentStatuses
                .map((g) => ({
                  ...g,
                  conversations: g.conversations.filter((c) => c.conversationId !== conversationId),
                }))
                .filter((g) => g.conversations.length > 0)
            })
            resolve()
          })
          .catch((err: _AxiosError) => {
            message.error('Failed to archive the conversation.')
            console.log(err)
            reject(err)
          })
      })
    },
    runMaguchiWorkflow(customerId: number) {
      axiosSalesAi
        .post(`/conversations/maguchi-workflow-runs/${customerId}`)
        .then(() => {
          message.success('Iris run queued.')
        })
        .catch((err: _AxiosError) => {
          message.error('Failed to start Iris.')
          console.log(err)
        })
    },
    // admin
    get_conversations(json: GetConversationsQuery) {
      axiosSalesAi
        .get('/conversations', { params: json })
        .then((res) => {
          this.$patch((state) => {
            state.conversations = res.data.items
            state.conversations_stats = res.data.meta
          })
        })
        .catch((err: _AxiosError) => {
          message.error('Failed to load data')
          console.log(err)
        })
    },
    get_conversation(id: number) {
      axiosSalesAi
        .get(`/conversations/${id}`)
        .then((res) => {
          this.$patch((state) => {
            state.currentConversation = res.data
          })
        })
        .catch((err: _AxiosError) => {
          message.error('Failed to load data')
          console.log(err)
        })
    },
    get_workflow_conversation(id: number) {
      axiosSalesAi
        .get<ConversationDetail>(`/conversations/${id}`)
        .then(({ data }) => {
          this.currentWorkflowConversation = data
        })
        .catch((err: _AxiosError) => {
          console.log(err)
        })
    },
    get_workflow_history(id: number) {
      axiosSalesAi
        .get<ConversationDetail>(`/conversations/history/${id}`)
        .then(({ data }) => {
          this.currentWorkflowConversation = data
        })
        .catch((err: _AxiosError) => {
          console.log(err)
        })
    },
    clear_current_workflow_conversation() {
      this.currentWorkflowConversation = null
    },
  },
})
